/**
 * useAdminAside — состояние правого aside нового админ-лейаута (admin-kit).
 * Контекстное наполнение: страница задаёт панель в onMounted и чистит в onBeforeUnmount,
 * лейаут (AdminAside) рендерит её данные. Декларативная схема (без teleport) —
 * SSR-безопасно: контент рендерится в <ClientOnly>, состояние ставится на клиенте.
 *
 * Auto-import: файл лежит в shared/model (см. nuxt.config imports.dirs).
 */
import { ref } from 'vue'
import type { HuiColor } from '@shared/ui/kit'

export interface AdminAsideStat {
  label: string
  value: string | number
  hint?: string
  color?: HuiColor
}

export interface AdminAsideAction {
  label: string
  icon?: string
  color?: HuiColor
  loading?: boolean
  onClick: () => void
}

export interface AdminAsidePanel {
  title?: string
  subtitle?: string
  stats?: AdminAsideStat[]
  /** Колонок в сетке показателей: 1 (для длинных подписей) или 2 (по умолчанию). */
  statsColumns?: 1 | 2
  actions?: AdminAsideAction[]
}

// Синглтон на уровне модуля. Панель ставится только на клиенте (onMounted),
// поэтому межзапросной утечки на SSR нет.
const panel = ref<AdminAsidePanel | null>(null)
const open = ref(true)
// Страница может спроецировать произвольный контент в aside через <Teleport to="#admin-aside-slot">.
// Тогда выставляет hasCustom=true — лейаут прячет декларативную панель/заглушку.
const hasCustom = ref(false)

export function useAdminAside() {
  function setPanel(p: AdminAsidePanel | null): void {
    panel.value = p
  }
  function clearPanel(): void {
    panel.value = null
  }
  function setCustom(value: boolean): void {
    hasCustom.value = value
  }
  function toggle(): void {
    open.value = !open.value
  }
  return { panel, open, hasCustom, setPanel, clearPanel, setCustom, toggle }
}
